/**
 * @file Copie un texte dans le presse-papiers et ne signale une réussite que si elle a eu lieu.
 * @module packages.common.hooks.useCopyToClipboard
 *
 * L'API presse-papiers moderne n'existe que dans un contexte sécurisé (HTTPS ou
 * localhost) : elle manque dès qu'on ouvre le dashboard depuis une IP du réseau
 * local. Le repli passe par l'événement `copy` du navigateur, dans lequel le
 * texte est écrit directement : il ne dépend ni d'une sélection ni du focus,
 * que le piège à focus d'une Dialog MUI annulerait. La réussite n'est annoncée
 * que si cet événement a réellement eu lieu, jamais sur la seule valeur de
 * retour de `execCommand`, qui peut répondre oui sans rien avoir copié.
 */

import {useState} from 'react';

/**
 * Copie un texte en l'écrivant dans l'événement `copy` déclenché par `execCommand`.
 *
 * @param {string} text - Texte à copier
 * @return {boolean} `true` seulement si le navigateur a émis l'événement et accepté le texte
 */
function copyThroughCopyEvent(text) {
  let written = false;
  const onCopy = (event) => {
    if (!event.clipboardData) return;
    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
    written = true;
  };

  document.addEventListener('copy', onCopy, true);
  try {
    document.execCommand('copy');
  } catch (error) {
    console.warn('Copy failed', error);
  } finally {
    document.removeEventListener('copy', onCopy, true);
  }
  return written;
}

/**
 * Hook de copie dans le presse-papiers.
 *
 * @return {{copiedText: (string|null), copy: function(string): Promise<boolean>}} Dernier texte
 *   copié avec succès, et fonction de copie qui résout `true` seulement si la copie a abouti
 *
 * @example
 * const {copy} = useCopyToClipboard();
 * if (await copy(secret)) enqueueSnackbar('Copié');
 */
export function useCopyToClipboard() {
  const [copiedText, setCopiedText] = useState(null);

  const copy = async (text) => {
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedText(text);
        return true;
      } catch (error) {
        // Permission refusée ou document non focalisé : le repli reste jouable.
        console.warn('Copy failed', error);
      }
    }

    const succeeded = copyThroughCopyEvent(text);
    setCopiedText(succeeded ? text : null);
    return succeeded;
  };

  return {copiedText, copy};
}
